import { useCallback, useMemo } from 'react';
import { QueryField, ValueEditorType } from '../../types/QueryBuilder';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { SIMPLE_QUERY_CATEGORY, getSimpleQueryFields } from '../../utils/queryBuilderUtils';
import { toList } from '../useApiQuery';
import { useCachedQuery } from './useCachedQuery';

interface ApiSegmentField {
    label: string;
    name: string;
    placeholder: string;
    defaultOperator: string;
    valueEditorType: ValueEditorType | '' | null;
    inputType: string | null;
    datatype: string;
    Categorie: string;
    values?: Array<{ label: string; name: string | null }>;
}

const resolveValueEditorType = (apiField: ApiSegmentField, inputType: string): ValueEditorType => {
    if (apiField.valueEditorType) return apiField.valueEditorType;
    if (inputType === 'date') return 'date';
    return apiField.values?.length ? 'select' : 'text';
};

const toQueryField = (apiField: ApiSegmentField): QueryField => {
    const inputType = apiField.inputType || 'text';
    return {
        name: apiField.name,
        label: apiField.label || apiField.placeholder,
        Categorie: apiField.Categorie,
        datatype: apiField.datatype === 'DATE' ? 'DATE' : apiField.inputType === 'number' ? 'NUMBER' : 'TEXT',
        defaultOperator: apiField.defaultOperator || '=',
        inputType,
        valueEditorType: resolveValueEditorType(apiField, inputType),
        values: apiField.values?.map(v => ({ label: v.label, name: v.name || '' })),
    };
};

const compareCategories = (a: string, b: string) => a.localeCompare(b, 'fr', { sensitivity: 'base' });

const NO_FIELDS: QueryField[] = [];

export const useSegmentFields = () => {
    const { authFetch, workspaceId } = useAuthenticatedFetchWithWorkspace();

    const fetchFields = useCallback(async (): Promise<QueryField[]> => {
        const apiFields = toList<ApiSegmentField>(await authFetch('/getDataQuerySegmentList', { method: 'GET' }));
        if (!apiFields.length) throw new Error('Aucun champ disponible');
        return apiFields.filter(f => f.name).map(toQueryField);
    }, [authFetch]);

    const { data: fields, loading, error } = useCachedQuery(
        `segment_fields:${workspaceId ?? 'none'}`,
        fetchFields,
        NO_FIELDS,
        'Erreur de chargement',
    );

    const categories = useMemo(
        () => [...new Set(fields.map(f => f.Categorie).filter(c => c && c !== SIMPLE_QUERY_CATEGORY))].sort(compareCategories),
        [fields]
    );

    const simpleFields = useMemo(() => getSimpleQueryFields(fields), [fields]);

    return useMemo(
        () => ({ fields, loading, error, categories, simpleFields }),
        [fields, loading, error, categories, simpleFields]
    );
};
